import { Component, EventEmitter, Input, Output } from "@angular/core";

import { MembershipBenefitInterface } from "src/app/share/interface/motor.interface";

/**
 * Presentational Drive+ membership card.
 *
 * Everything it shows is derived from `tiers` + `allBenefits` + `selectedCode`,
 * so the same component renders the inline card on the add-ons page and the copy
 * inside the mobile confirmation drawer, and switching tier inside the drawer
 * recomputes prices and benefits without the parent being involved.
 *
 * It owns no state and performs no pricing that reaches the order total - the
 * page remains the single source of truth for what the customer is charged.
 */
@Component({
  selector: "app-driveplus-card",
  templateUrl: "./driveplus-card.component.html",
  styleUrls: ["./driveplus-card.component.scss"]
})
export class DrivePlusCardComponent {
  /** Lite / Standard / Premium, in display order. */
  @Input() tiers: any[] = [];

  /** Every membership benefit row; filtered per tier internally. */
  @Input() allBenefits: MembershipBenefitInterface[] = [];

  /** Code of the tier to display, e.g. "DrivePlusStandard". */
  @Input() selectedCode: string;

  /** Undiscounted road tax, shown struck through. */
  @Input() originalRoadTaxPrice: number;

  /**
   * "insurance" or "takaful", already lowercased. Needed by the benefit strings
   * that interpolate {{protectionPlan}}; the wording depends on the quotation,
   * which only the page knows.
   */
  @Input() protectionPlanWording = "";

  /** Whether the selection checkbox is rendered at the card's top-right. */
  @Input() showSelectionCheckbox = true;

  /** State of that checkbox. */
  @Input() selected = true;

  @Output() tierChange = new EventEmitter<any>();
  @Output() selectedChange = new EventEmitter<boolean>();

  // Order to fall back on when the API gives rows the same `seq`.
  private static readonly BENEFIT_ORDER = [
    "RoadTaxDiscount",
    "BatteryReplacementVoucher",
    "ServiceMaintenanceVoucher",
    "DashcamVoucher",
    "TintingVoucher",
    "CarRentalDiscount",
    "CarReplacementVoucher"
  ];

  // Not customer-facing on this card.
  private static readonly BENEFIT_EXCLUDED = ["AtomeDiscount"];

  get tier(): any {
    return (
      this.tiers?.find((t) => t?.code === this.selectedCode) ??
      this.tiers?.[0] ??
      null
    );
  }

  get tierClass(): string {
    switch (this.tier?.code) {
      case "DrivePlusLite":
        return "lite";
      case "DrivePlusStandard":
        return "standard";
      case "DrivePlusPremium":
        return "premium";
      default:
        return "lite";
    }
  }

  get benefits(): MembershipBenefitInterface[] {
    const rank = (code: string) => {
      const i = DrivePlusCardComponent.BENEFIT_ORDER.indexOf(code);
      // Unknown codes sort last - never first, which is what
      // `indexOf(...) - indexOf(...)` would have done via -1.
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };

    const code = this.tier?.code;

    return (this.allBenefits ?? [])
      .filter((b) => b.motorMembershipCode === code)
      .filter(
        (b) => !DrivePlusCardComponent.BENEFIT_EXCLUDED.includes(b.code)
      )
      .sort(
        (a, b) => (a.seq ?? 0) - (b.seq ?? 0) || rank(a.code) - rank(b.code)
      );
  }

  get price(): number {
    return this.parseMoney(this.tier?.evaluate);
  }

  get wasPrice(): number {
    return this.parseMoney(this.tier?.oriEvaluate);
  }

  get savings(): number {
    return this.wasPrice - this.price;
  }

  /**
   * Road tax payable on this tier. The API overwrites the RoadTaxDiscount row's
   * `evaluate` with the after-discount price (see
   * usp_Motor_Membership_GetMotorMembership), so this is a plain lookup.
   */
  get roadtaxPrice(): number {
    return this.parseMoney(
      this.benefits.find((b) => b.code === "RoadTaxDiscount")?.evaluate
    );
  }

  get isRoadtaxFree(): boolean {
    return this.roadtaxPrice === 0;
  }

  /**
   * Summed from the rows actually rendered below it, so the headline can never
   * drift from the list. Road tax contributes its cap, not its `evaluate`,
   * which is the post-discount price rather than the value of the benefit.
   */
  get loyaltyRewardsTotal(): number {
    return this.benefits.reduce((sum, b) => sum + this.benefitAmount(b), 0);
  }

  benefitAmount(benefit: MembershipBenefitInterface): number {
    return benefit?.maxCapAmount != null
      ? this.parseMoney(benefit.maxCapAmount)
      : this.parseMoney(benefit?.evaluate);
  }

  tierLabelKey(tier: any): string {
    return (
      "plans.motorMembership.tier" +
      String(tier?.code ?? "").replace("DrivePlus", "")
    );
  }

  isTierSelected(tier: any): boolean {
    return tier?.code === this.tier?.code;
  }

  // One i18n key per benefit code, so a new benefit is a translation-only change.
  benefitLabelKey(benefit: MembershipBenefitInterface): string {
    const suffix =
      benefit.code === "RoadTaxDiscount" && benefit.maxCapAmount != null
        ? "Cap"
        : "";
    return "plans.motorMembership.DrivePlusBenefits." + benefit.code + suffix;
  }

  /**
   * Superset of params; ngx-translate ignores the ones a string does not use.
   *
   * Every placeholder used by any DrivePlusBenefits.* string must appear here -
   * a missing one renders as an empty gap with no error. Current consumers:
   *   amount             -> the new "(RM…)" voucher labels
   *   discountPrice      -> EPPDiscount, NextRenewalDiscount(Old)
   *   protectionPlan     -> PrioritisedAssistance, NextRenewalDiscountOld
   *   discountPercentage -> legacy RoadTaxDiscount copy
   */
  benefitLabelParams(benefit: MembershipBenefitInterface): any {
    return {
      amount: this.benefitAmount(benefit).toFixed(2),
      discountPrice: benefit.evaluate,
      protectionPlan: this.protectionPlanWording,
      discountPercentage: benefit.discountPercentage,
      maxCapAmount: benefit.maxCapAmount
    };
  }

  onTierClick(tier: any): void {
    this.tierChange.emit(tier);
  }

  onSelectedChange(checked: boolean): void {
    this.selectedChange.emit(checked);
  }

  private parseMoney(value: unknown): number {
    const parsed = Number.parseFloat(String(value ?? ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
}
